import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { type LifeSpace, type CreateLifeSpaceData } from '../../../hooks/useLifeSpaces'
import { getLifeSpaceIcon, AVAILABLE_ICON_NAMES } from '../../../lib/icons/iconRegistry'
import './CreateLifeSpaceDialog.scss'

const defaultColors = [
  '#FF6B6B', // Red
  '#4ECDC4', // Teal
  '#45B7D1', // Blue
  '#96CEB4', // Green
  '#FFA07A', // Light Coral
  '#DDA0DD', // Plum
  '#F4A460', // Sandy Brown
  '#98D8C8', // Mint
  '#FFD93D', // Yellow
  '#6C63FF', // Purple
]

interface CreateLifeSpaceDialogProps {
  editingSpace?: LifeSpace | null
  onConfirm: (data: CreateLifeSpaceData) => void
  onCancel: () => void
}

const CreateLifeSpaceDialogComponent: React.FC<CreateLifeSpaceDialogProps> = ({
  editingSpace,
  onConfirm,
  onCancel
}) => {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [name, setName] = useState(editingSpace?.name || '')
  const [selectedIcon, setSelectedIcon] = useState(editingSpace?.iconName || 'Star')
  const [selectedColor, setSelectedColor] = useState(editingSpace?.color || defaultColors[0])
  const [errors, setErrors] = useState<{ name?: string }>({})

  const validate = useCallback((): boolean => {
    const newErrors: { name?: string } = {}

    if (!name.trim()) {
      newErrors.name = t('lifeSpaces.nameRequired')
    } else if (name.length > 20) {
      newErrors.name = t('lifeSpaces.nameMaxLength')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [name, t])

  const handleConfirm = useCallback(() => {
    if (!validate()) return

    onConfirm({
      name: name.trim(),
      iconName: selectedIcon,
      color: selectedColor
    })
  }, [validate, name, selectedIcon, selectedColor, onConfirm])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && name.trim()) {
      handleConfirm()
    } else if (e.key === 'Escape') {
      onCancel()
    }
  }, [name, handleConfirm, onCancel])

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value)
  }, [])

  const handleIconSelect = useCallback((iconName: string) => {
    setSelectedIcon(iconName)
  }, [])

  const handleColorSelect = useCallback((color: string) => {
    setSelectedColor(color)
  }, [])

  const dialogTitle = editingSpace ? t('lifeSpaces.editLifeSpace') : t('lifeSpaces.newLifeSpace')
  const buttonText = editingSpace ? t('lifeSpaces.save') : t('lifeSpaces.create')
  const inputClassName = `create-life-space-dialog__input ${errors.name ? 'create-life-space-dialog__input--error' : ''}`

  // Memoize icon grid items
  const iconGridItems = useMemo(() => {
    return AVAILABLE_ICON_NAMES.map((iconName) => {
      const Icon = getLifeSpaceIcon(iconName)
      const isSelected = selectedIcon === iconName

      return (
        <button
          key={iconName}
          className={`create-life-space-dialog__icon-button ${isSelected ? 'create-life-space-dialog__icon-button--selected' : ''}`}
          onClick={() => handleIconSelect(iconName)}
          style={{ color: selectedColor }}
        >
          <Icon size={20} />
        </button>
      )
    })
  }, [selectedIcon, selectedColor, handleIconSelect])

  // Memoize color grid items
  const colorGridItems = useMemo(() => {
    return defaultColors.map((color) => {
      const isSelected = selectedColor === color

      return (
        <button
          key={color}
          className={`create-life-space-dialog__color-button ${isSelected ? 'create-life-space-dialog__color-button--selected' : ''}`}
          onClick={() => handleColorSelect(color)}
          style={{ backgroundColor: color }}
        />
      )
    })
  }, [selectedColor, handleColorSelect])

  // Auto focus on name input
  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 100)
    return () => clearTimeout(timer)
  }, [])

  const PreviewIcon = getLifeSpaceIcon(selectedIcon)

  return (
    <>
      <div className="create-life-space-dialog__backdrop" onClick={onCancel} />
      <div className="create-life-space-dialog" onKeyDown={handleKeyDown}>
        <div className="create-life-space-dialog__header">
          <h2 className="create-life-space-dialog__title">
            {dialogTitle}
          </h2>
        </div>

        <div className="create-life-space-dialog__content">
          {/* Name Input */}
          <div className="create-life-space-dialog__field">
            <label>{t('lifeSpaces.nameLabel')}</label>
            <input
              ref={inputRef}
              type="text"
              className={inputClassName}
              value={name}
              onChange={handleNameChange}
              placeholder={t('lifeSpaces.namePlaceholder')}
              maxLength={20}
              autoFocus
            />
            {errors.name && (
              <span className="create-life-space-dialog__error">{errors.name}</span>
            )}
            <span className="create-life-space-dialog__char-count">
              {name.length}/20
            </span>
          </div>

          {/* Icon Selection */}
          <div className="create-life-space-dialog__field">
            <label>{t('lifeSpaces.iconLabel')}</label>
            <div className="create-life-space-dialog__icon-grid">
              {iconGridItems}
            </div>
          </div>

          {/* Color Selection */}
          <div className="create-life-space-dialog__field">
            <label>{t('lifeSpaces.colorLabel')}</label>
            <div className="create-life-space-dialog__color-grid">
              {colorGridItems}
            </div>
          </div>

          {/* Preview */}
          <div className="create-life-space-dialog__preview">
            <label>{t('lifeSpaces.previewLabel')}</label>
            <div className="create-life-space-dialog__preview-space">
              <div
                className="life-space-button-preview"
                style={{ backgroundColor: `${selectedColor}20` }}
              >
                <PreviewIcon size={24} color={selectedColor} />
              </div>
              <span>{name || t('lifeSpaces.previewDefault')}</span>
            </div>
          </div>
        </div>

        <div className="create-life-space-dialog__actions">
          <button
            className="create-life-space-dialog__button create-life-space-dialog__button--secondary"
            onClick={onCancel}
          >
            {t('lifeSpaces.cancel')}
          </button>
          <button
            className="create-life-space-dialog__button create-life-space-dialog__button--primary"
            onClick={handleConfirm}
            disabled={!name.trim()}
          >
            {buttonText}
          </button>
        </div>
      </div>
    </>
  )
}

/**
 * Modal dialog for creating or editing a custom Life Space.
 *
 * @description
 * Renders a modal overlay with fields for name (max 20 chars), icon selection
 * (26 Lucide icons from the registry), color selection (10 preset colors),
 * and a live preview. Supports both create and edit mode based on the
 * `editingSpace` prop.
 *
 * Keyboard: `Enter` confirms, `Escape` cancels.
 *
 * @example
 * // Create mode
 * <CreateLifeSpaceDialog
 *   onConfirm={(data) => addSpace(data)}
 *   onCancel={() => setShowDialog(false)}
 * />
 *
 * @example
 * // Edit mode
 * <CreateLifeSpaceDialog
 *   editingSpace={selectedSpace}
 *   onConfirm={(data) => updateSpace(selectedSpace.id, data)}
 *   onCancel={() => setShowDialog(false)}
 * />
 */
export const CreateLifeSpaceDialog = React.memo(CreateLifeSpaceDialogComponent)
